import { denormalisedResponseEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { uploadBannerImage as uploadBannerImageAPI } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPLOAD_BANNER_REQUEST = 'app/ProfileSettingsPage/UPLOAD_BANNER_REQUEST';
export const UPLOAD_BANNER_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_BANNER_SUCCESS';
export const UPLOAD_BANNER_ERROR = 'app/ProfileSettingsPage/UPLOAD_BANNER_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const UPDATE_FEATURED_LISTINGS_REQUEST = 'app/ProfileSettingsPage/UPDATE_FEATURED_LISTINGS_REQUEST';
export const UPDATE_FEATURED_LISTINGS_SUCCESS = 'app/ProfileSettingsPage/UPDATE_FEATURED_LISTINGS_SUCCESS';
export const UPDATE_FEATURED_LISTINGS_ERROR = 'app/ProfileSettingsPage/UPDATE_FEATURED_LISTINGS_ERROR';

export const FETCH_LISTINGS_REQUEST = 'app/ProfileSettingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ProfileSettingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ProfileSettingsPage/FETCH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  bannerImage: null,
  uploadBannerError: null,
  uploadBannerInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  updatingFeaturedListings: false,
  updateFeaturedListingsError: null,
  listings: [],
  listingsInProgress: false,
  listingsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPLOAD_BANNER_REQUEST:
      return {
        ...state,
        bannerImage: { ...payload.params },
        uploadBannerInProgress: true,
        uploadBannerError: null,
      };
    case UPLOAD_BANNER_SUCCESS: {
      const { id, imageUrl } = payload;
      const { file } = state.bannerImage || {};
      const bannerImage = { id, imageUrl, file };
      return { ...state, bannerImage, uploadBannerInProgress: false };
    }
    case UPLOAD_BANNER_ERROR: {
      return { 
        ...state, 
        bannerImage: null, 
        uploadBannerInProgress: false, 
        uploadBannerError: payload.error 
      };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        bannerImage: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null, uploadBannerError: null };

    case UPDATE_FEATURED_LISTINGS_REQUEST:
      return {
        ...state,
        updatingFeaturedListings: true,
        updateFeaturedListingsError: null,
      };

    case UPDATE_FEATURED_LISTINGS_SUCCESS:
      return {
        ...state,
        updatingFeaturedListings: false,
        updateFeaturedListingsError: null,
      };

    case UPDATE_FEATURED_LISTINGS_ERROR:
      return {
        ...state,
        updatingFeaturedListings: false,
        updateFeaturedListingsError: payload,
      };

    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        listingsInProgress: true,
        listingsError: null,
      };

    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listings: payload.data,
        listingsInProgress: false,
        listingsError: null,
      };

    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        listingsInProgress: false,
        listingsError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// Banner upload action creators
export const uploadBannerRequest = params => ({ type: UPLOAD_BANNER_REQUEST, payload: { params } });
export const uploadBannerSuccess = result => ({ type: UPLOAD_BANNER_SUCCESS, payload: result });
export const uploadBannerError = error => ({
  type: UPLOAD_BANNER_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// Featured listings action creators
export const updateFeaturedListingsRequest = () => ({
  type: UPDATE_FEATURED_LISTINGS_REQUEST,
});

export const updateFeaturedListingsSuccess = () => ({
  type: UPDATE_FEATURED_LISTINGS_SUCCESS,
});

export const updateFeaturedListingsError = e => ({
  type: UPDATE_FEATURED_LISTINGS_ERROR,
  payload: e,
  error: true,
});

// Listings action creators
export const fetchListingsRequest = () => ({
  type: FETCH_LISTINGS_REQUEST,
});

export const fetchListingsSuccess = data => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data },
});

export const fetchListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  payload: e,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

// Banner upload thunk
export function uploadBannerImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadBannerRequest(actionPayload));

    const formData = new FormData();
    formData.append('file', actionPayload.file);

    return uploadBannerImageAPI(formData)
      .then(result => {
        // Parse the response
        const data = result.data || result;
        if (data?.imageUrl) {
          dispatch(uploadBannerSuccess({ id, imageUrl: data.imageUrl }));
        } else {
          throw new Error('Upload failed - no image URL returned');
        }
      })
      .catch(e => dispatch(uploadBannerError({ id, error: storableError(e) })));
  };
}

// Helper function to update listings' featured status
const updateListingsFeaturedStatus = async (updates, sdk) => {
  for (const { id, isFeatured } of updates) {
    try {
      await sdk.ownListings.update({
        id: new UUID(id),
        publicData: { isFeatured }
      }, { expand: true });
    } catch (error) {
      console.error(`Failed to update listing ${id}:`, error);
      // Continue with other listings even if one fails
    }
  }
};

export const updateFeaturedListings = (featuredListings, previousFeaturedListings = []) => {
  return (dispatch, getState, sdk) => {
    dispatch(updateFeaturedListingsRequest());
    
    const { currentUser } = getState().user;
    const currentPublicData = currentUser.attributes.profile.publicData || {};
    
    // Update user's publicData
    const userPayload = {
      publicData: {
        ...currentPublicData,
        featuredListings
      }
    };
    
    // Determine which listings need isFeatured updates
    const currentIds = featuredListings.map(fl => fl.listingId);
    const previousIds = previousFeaturedListings.map(fl => fl.listingId);
    const toAdd = currentIds.filter(id => !previousIds.includes(id));
    const toRemove = previousIds.filter(id => !currentIds.includes(id));
    
    // Update user profile first
    return sdk.currentUser
      .updateProfile(userPayload, {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        // Update current user in Redux store
        const entities = denormalisedResponseEntities(response);
        const updatedUser = entities[0];
        dispatch(currentUserShowSuccess(updatedUser));
        
        // Update listing isFeatured flags (using numbers for sorting)
        const listingUpdates = [
          ...toAdd.map(id => ({ id, isFeatured: 1 })),
          ...toRemove.map(id => ({ id, isFeatured: 0 }))
        ];
        
        // Update listings sequentially to avoid rate limits
        return updateListingsFeaturedStatus(listingUpdates, sdk);
      })
      .then(() => {
        dispatch(updateFeaturedListingsSuccess());
      })
      .catch(e => {
        dispatch(updateFeaturedListingsError(storableError(e)));
        throw e;
      });
  };
};

export const fetchListings = () => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchListingsRequest());
    
    const params = {
      perPage: 100, // Get enough listings for featured selection
      pub_isVariant: false,
      sort: 'pub_isFeatured',
      include: ['images'],
      'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      'limit.images': 1,
    };
    
    return sdk.ownListings
      .query(params)
      .then(response => {
        // Filter out variants
        const filteredData = response.data.data.filter(listing => !listing.attributes.publicData.isVariant);
        dispatch(fetchListingsSuccess(filteredData));
        return filteredData;
      })
      .catch(e => {
        dispatch(fetchListingsError(storableError(e)));
        throw e;
      });
  };
};
